import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { AppDispatch } from '@/app/store/store';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import { getPageApiBaseUrl } from '@/core/utils/apiUtils';
import type { PosCartRepository, FetchPosCartItemsArgs } from '../../domain/interfaces/PosCartRepository';
import type { PosCartItem } from '../../domain/models/PosCartItem';
import type { PosCartItemDTO } from '../dto/posCart.dto';
import {
  posCartItemDtoToDomain,
  posCartItemDomainToPlain,
  posCartItemPlainToDomain,
} from '../mappers/posCart.mapper';

const posCartApiSlice = createApiSlice({
  clientId: 'posCartApi',
  tagTypes: ['PosCart'],
  endpoints: {
    getPosCartItems: {
      url: (args: unknown) => {
        const { customerCode, cartItemsOnly, includeWishlist, includeWebCart, page, size } = (args || {}) as FetchPosCartItemsArgs;
        const params = new URLSearchParams();
        params.append('customerCode', customerCode);
        if (cartItemsOnly !== undefined) params.append('cartItemsOnly', String(cartItemsOnly));
        if (includeWishlist !== undefined) params.append('includeWishlist', String(includeWishlist));
        if (includeWebCart !== undefined) params.append('includeWebCart', String(includeWebCart));
        if (page !== undefined) params.append('page', String(page));
        if (size !== undefined) params.append('size', String(size));

        return `${getPageApiBaseUrl()}/pos-cart/items?${params.toString()}`;
      },
      method: 'GET',
      transformResponse: (response: unknown): Record<string, unknown> => {
        const rawItems = Array.isArray(response) ? (response as PosCartItemDTO[]) : [];
        const domainItems = rawItems.map(posCartItemDtoToDomain);
        const plainItems = domainItems.map(posCartItemDomainToPlain);
        return { items: plainItems } as unknown as Record<string, unknown>;
      },
      keepUnusedDataFor: 60,
    },
  },
});

export const posCartApi = posCartApiSlice.queryClient;
export const posCartApiRedux = posCartApiSlice.queryClient as ReduxQueryClient;

export class PosCartAdapter implements PosCartRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchPosCartItems(args: FetchPosCartItemsArgs): Promise<PosCartItem[]> {
    const initiateResult = posCartApi.endpoints.getPosCartItems.initiate(args);
    const res = await executeQuery<{ items: Record<string, unknown>[] }>(initiateResult, this.dispatch);
    const plainItems = res?.items ?? [];
    return plainItems.map(posCartItemPlainToDomain);
  }
}
